import clsx from 'clsx';

export type PhaseStatus = 'past' | 'current' | 'future';

export interface PhaseMetric {
  label: string;
  value: string;
}

export interface PhaseCardProps {
  /** `01` / `02` / `03`. Two-digit zero-padded label rendered above the title. */
  stepNumber: string;
  title: string;
  /** Optional prose narrative — rendered as a single paragraph between the header and metrics. */
  body?: string;
  /**
   * Per-card metric pairs rendered as a label:value list at the bottom of
   * the card (mirrors Lovable's `PhaseCard` at PublicDealView.tsx:826-840).
   * When omitted, falls back to splitting `body` lines for back-compat.
   */
  metrics?: PhaseMetric[];
  status: PhaseStatus;
  className?: string;
}

const STATUS_TOKEN: Record<
  PhaseStatus,
  { title: string; body: string; label: string; opacity: string }
> = {
  past: {
    title: 'text-text-bright',
    body: 'text-text',
    label: 'text-accent',
    opacity: 'opacity-95',
  },
  current: {
    title: 'text-text-bright',
    body: 'text-text',
    label: 'text-accent',
    opacity: '',
  },
  future: {
    title: 'text-text-bright',
    body: 'text-text',
    label: 'text-accent',
    opacity: 'opacity-85',
  },
};

/**
 * Value-Add Story phase card — Lovable-parity rewrite (2026-05-12).
 *
 * Mirrors `dashboard-arcanum/src/pages/PublicDealView.tsx:787-844`. Each card
 * is dense: header row `[PHASE 01]——hairline——[LABEL]`, then a single
 * narrative `<p>`, then a footer of label:value metric pairs separated from
 * the body by a top-border. Left edge carries a 2px accent strip; outer
 * border is the standard panel hairline.
 */
export function PhaseCard({
  stepNumber,
  title,
  body,
  metrics,
  status,
  className,
}: PhaseCardProps) {
  const tokens = STATUS_TOKEN[status];

  return (
    <div
      data-testid="phase-card"
      data-status={status}
      className={clsx(
        'relative flex flex-col gap-[5px] px-2.5 py-2 bg-panel-bg border border-panel-border border-l-2 border-l-accent h-full min-w-0',
        tokens.opacity,
        className,
      )}
    >
      {/* Header — PHASE NN + hairline + label */}
      <div className="flex items-baseline gap-2 min-w-0">
        <span
          className={clsx(
            'text-[9px] font-mono font-bold tracking-[1.3px] uppercase shrink-0',
            tokens.label,
          )}
        >
          PHASE {stepNumber}
        </span>
        <span className="flex-1 h-px bg-panel-border" />
        <span
          className={clsx(
            'text-[9.5px] font-mono font-bold tracking-[1.5px] uppercase shrink-0 truncate',
            tokens.title,
          )}
        >
          {title}
        </span>
      </div>

      {/* Narrative */}
      {body && (
        <p
          className={clsx(
            'text-[10px] font-body leading-[1.35] font-normal m-0 whitespace-pre-line',
            tokens.body,
          )}
        >
          {body}
        </p>
      )}

      {/* Metrics — fills bottom of the card */}
      {metrics && metrics.length > 0 && (
        <div className="flex flex-col mt-auto pt-1 border-t border-panel-border">
          {metrics.map((m, i) => (
            <div
              key={`${m.label}-${i}`}
              className="flex justify-between items-baseline py-[2px] min-w-0 gap-2"
            >
              <span className="text-[8.5px] font-mono text-text-muted tracking-[0.5px] uppercase truncate">
                {m.label}
              </span>
              <span className="text-[11px] font-mono text-accent font-semibold tabular-nums shrink-0">
                {m.value}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
